import { Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from 'app/core/services/storage/storage.service';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, finalize, map, takeUntil, takeWhile } from 'rxjs/operators';
import { SiteConfig } from '../../config/site-config';
import { IDigitalWalletBalance } from '../account/digital-wallet/models/digital-wallet-balance.interface';
import { DigitalWalletService } from '../account/digital-wallet/services/digital-wallet.service';
import { AppState } from '../app.state';
import { UserRoutes } from '../core/models/user-routes.model';
import { UserRouteService } from '../core/services/user-routes/user-routes.service';
import { CostEstimatorComponent } from '../cost-estimator/cost-estimator.component';
import { DeliveryDefenseService } from '../delivery-defense/services/delivery-defense.service';
import {
    TrackingDetailsDialogComponent,
} from '../history/report-history/tracking-details-dialog/tracking-details-dialog.component';
import { RecentShipmentService } from '../history/services/recent-shipment.service';
import { IImageRotatorConfig } from '../shared/components/image-rotator/models/image-rotator-config.interface';
import { S3FileDownloaderComponent } from '../shared/components/s3-file-downloader/s3-file-downloader.component';
import { Customer } from '../shared/models/customer/customer.model';
import { NotificationType } from '../shared/models/notification-type';
import { IQuickTrackShipment } from '../shared/models/shipments/quick-track-shipment.interface';
import { AnnouncementService } from '../shared/services/announcement/annoucement.service';
import { IAnnouncement } from '../shared/services/announcement/models/announcement.interface';
import { IEvent } from '../shared/services/announcement/models/event.interface';
import { INews } from '../shared/services/announcement/models/news.interface';
import { BannerService } from '../shared/services/banner/banner.service';
import { BannerType } from '../shared/services/banner/models/banner-type.enum';
import { IBanner } from '../shared/services/banner/models/banner.interface';
import { ErrorHandlerService } from '../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../shared/services/notification/notification.service';
import { User } from '../shared/services/user/models/user.model';
import { UserService } from '../shared/services/user/user.service';
import { UtilityService } from '../shared/services/utility/utility.service';
import { IDDSubscription } from '../ship/models/dd-subscription.interface';
import { Package } from '../ship/models/package.model';
import { ShipmentService } from '../ship/services/shipment.service';
import { DefencePopupComponent } from './defence-popup/defence-popup.component';
import { DataTableDirective } from 'angular-datatables';

@Component({
    selector: 'upsc-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
    @ViewChild('packagingChecklistDownloader') public packagingChecklistDownloader: S3FileDownloaderComponent;

    public user: User;
    public customer: Customer;
    public isDEUser = false;
    public isGBUser = false;
    public isEUUser: boolean = false;
    public showGiftBox = false;
    public announcements: IAnnouncement[];
    public isAnnouncementsLoading = false;

    public upcomingEvents: IEvent[];
    public isUpcomingEventsLoading = false;

    public news: INews[];
    public isNewsLoading = false;

    public banners: any[];
    public isBannersLoading = false;
    public isSideBannerAvailable = false;
    public giftClosebtn = false;
    public quickTrackShipments: IQuickTrackShipment[];
    public isGettingQuickTrackShipments = false;
    public dtOptions: any = {};
    public dtTrigger: Subject<any> = new Subject<any>();

    public isGettingShipmentDetails = false;
    public userRoutes: UserRoutes;
    public isDDEnabled = false;
    public getComplimentaryScores = false;
    public isUSUser = false;
    public isDWCustomer = false;
    public isRewardShipPointRetrieving$ = signal<boolean>(false);
    public isRewardShipPointError$ = signal<boolean>(false);
    public rewardShipPoint$ = signal<number>(0);
    private isEmpty = true;
    private ngDestroyed$ = new Subject<void>();

    @ViewChild(DataTableDirective, {static: false})
    dtElement: DataTableDirective;

    now = new Date();
    prevMonthLastDate = new Date(this.now.getFullYear(), this.now.getMonth(), 0).toLocaleDateString();
    prevMonthFirstDate = new Date(
        this.now.getFullYear() - (this.now.getMonth() > 0 ? 0 : 1),
        (this.now.getMonth() - 1 + 12) % 12,
        1).toLocaleDateString();

    public isPackagingChecklistDownloading = false;
    public dashboardBannerConfig: IImageRotatorConfig = SiteConfig.dashboardBanner;
    public dashboardAnnouncementConfig = SiteConfig.dashboardAnnouncement;

    private getUserRoutesSubscription: Subscription;
    private getUserLatestShipmentsSubscription: Subscription;
    private getDashboardAnnouncementsSubscription: Subscription;
    private getEventsSubscription: Subscription;
    private getNewsSubscription: Subscription;
    private getBannersSubscription: Subscription;

    private customerSubscription: IDDSubscription;

    private country: string;

    public constructor(private announcementService: AnnouncementService,
                       private bannerService: BannerService,
                       private notificationService: NotificationService,
                       private recentShipmentService: RecentShipmentService,
                       private shipmentService: ShipmentService,
                       private readonly errorHandlerService: ErrorHandlerService,
                       private utilityService: UtilityService,
                       private userService: UserService,
                       private translateService: TranslateService,
                       public storageService: StorageService,
                       private router: Router,
                       private dialog: MatDialog,
                       private readonly digitalWalletService: DigitalWalletService,
                       private readonly cookieService: CookieService,
                       private readonly deliveryDefenseService: DeliveryDefenseService,
                       private readonly userRouteService: UserRouteService,
                       public readonly appState: AppState,
    ) {
        // console.log(this.router.getCurrentNavigation()?.extras.state.user);
        this.translateService.onLangChange.subscribe(() =>{
            this.updateDTOptionsLanguage();
        });
        this.dashboardBannerConfig.images = [];
    }

    public get canSeeRewardShip(): boolean {
        return (this.isDWCustomer)
            && !this.user?.IsGuest;
    }
    rerender(): void {
        this.dtElement.dtInstance?.then((dtInstance: DataTables.Api) => {
            // Destroy the table first
            if(this.isEmpty) {
                dtInstance.destroy();
            }
            
            // Call the dtTrigger to rerender again
            this.dtTrigger.next(null);
        });
    }
    public updateDTOptionsLanguage(): void{
        this.dtOptions.language = {
                emptyTable: this.translateService.instant('dashboard.noShipments'),
            };

        this.rerender();
    }

    public ngOnInit(): void {
        this.user = this.appState.user$();
        this.customer = this.appState.customer$();
        this.userRoutes = this.appState.userRoutes$();

        if (!this.userRoutes) {
            this.userRoutes = this.userRouteService.loadUserRoutes(this.user, this.customer);
            this.appState.userRoutes$.set(this.userRoutes);
        }

        this.handleGetUserSuccess(this.user);
        this.handleGetCustomerSuccess(this.customer);

        this.dtOptions = SiteConfig.tableConfig;
        this.dtOptions.language = {
            emptyTable: this.translateService.instant('dashboard.noShipments'),
        };

        this.utilityService.clearSubscriptions([this.getUserLatestShipmentsSubscription]);
        this.isGettingQuickTrackShipments = true;
        this.getUserLatestShipmentsSubscription = this.recentShipmentService.getUserLatestShipments()
                                                      .pipe(
                                                          catchError((err) => {
                                                              this.handleGetUserLatestShipmentsFailure(err);
                                                              return of(null);
                                                          }),
                                                          takeWhile(() => !this.quickTrackShipments),
                                                      )
                                                      .subscribe(
                                                          shipments => this.handleGetUserLatestShipmentsSuccess(shipments),
                                                      );

        this.utilityService.clearSubscriptions([this.getEventsSubscription]);
        this.isUpcomingEventsLoading = true;
        this.getEventsSubscription = this.announcementService.getEvents()
                                         .pipe(
                                             catchError((err) => {
                                                 this.handleGetUpcomingEventsFailure(err);
                                                 return of(null);
                                             }),
                                             takeWhile(() => !this.upcomingEvents),
                                         )
                                         .subscribe(
                                             events => this.handleGetUpcomingEventsSuccess(events),
                                         );

        this.utilityService.clearSubscriptions([this.getNewsSubscription]);
        this.isNewsLoading = true;
        const country = this.country || 'US';
        this.getNewsSubscription = this.announcementService.getNews(country)
                                       .pipe(
                                           catchError((err) => {
                                               this.handleGetNewsFailure(err);
                                               return of(null);
                                           }),
                                           takeWhile(() => !this.news),
                                       )
                                       .subscribe(
                                           news => this.handleGetNewsSuccess(news),
                                       );

        if (this.customer?.DWEnabled) {
            this.getDWBalance();
        }

        this.digitalWalletService.getCheckDWCustomer()
            .subscribe(
                (isDWCustomer) => {
                    this.isDWCustomer = isDWCustomer;
                },
            );

        this.deliveryDefenseService.getCustomerSubscription()
            .subscribe(
                (subscription) => {
                    this.customerSubscription = subscription;
                },
            );
    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();

        this.utilityService.clearSubscriptions([
            this.getUserRoutesSubscription,
            this.getUserLatestShipmentsSubscription,
            this.getDashboardAnnouncementsSubscription,
            this.getEventsSubscription,
            this.getNewsSubscription,
        ]);
    }

    private getDWBalance(): void {
        this.isRewardShipPointRetrieving$.set(true);
        this.digitalWalletService.getDWBalance(this.customer?.CustomerId)
            .pipe(
                takeUntil(this.ngDestroyed$),
                catchError((err) => {
                    this.errorHandlerService.logHttpError(err);
                    this.isRewardShipPointError$.set(true);
                    return of(null);
                }),
                map(res => res?.data),
                finalize(() => {
                    this.isRewardShipPointRetrieving$.set(false);
                }),
            )
            .subscribe(
                (res: IDigitalWalletBalance) => {
                    if (res) {
                        this.isRewardShipPointError$.set(false);
                    }

                    this.rewardShipPoint$.set(res?.available_points || 0);
                }
            );
    }

    public openCostEstimator(event): void {
        event.preventDefault();
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            // height: '600px',
            width: '800px',
            maxWidth: '100%',
            panelClass: ['mobile-fullscreen-dialog'],
        };

        const dialogRef: MatDialogRef<CostEstimatorComponent> = this.dialog.open(CostEstimatorComponent, dialogConfig);
    }

    public open_gift() {
        document.getElementById('giftPanel').className += ' active';
        this.getComplimentaryScores = true;
        this.giftClosebtn = true;

    }

    public close_gift() {
        document.getElementById('giftPanel').classList.remove('active');
        this.getComplimentaryScores = false;
        this.giftClosebtn = false;
    }

    public signUp() {
        this.router.navigate(['/delivery-defense-sign-up']);
    }

    public getShipmentRecipient(shipment: IQuickTrackShipment) {
        if (shipment.ShipToFirstName && shipment.ShipToFirstName.trim()) {
            return `${ shipment.ShipToFirstName } ${ shipment.ShipToLastName }`;
        }

        return shipment.ShipToCompanyName;
    }

    public openTrackedShipmentDialog(event, quickTrackShipment: IQuickTrackShipment) {
        event.preventDefault();

        this.isGettingShipmentDetails = true;
        this.shipmentService.getShipment(quickTrackShipment.ShipmentId)
            .subscribe(
                shipment => this.handleGetShipmentSuccess(shipment),
                err => this.handleGetShipmentFailure(err),
            );
    }

    public getCurrentYear() {
        return (new Date()).getFullYear();
    }

    public downloadPackagingChecklist(event) {
        event.preventDefault();

        this.isPackagingChecklistDownloading = true;
        this.packagingChecklistDownloader.downloadFile(event);
    }

    public onPackagingChecklistDownloadEnded(isDownloadEnded: boolean) {
        this.isPackagingChecklistDownloading = false;
    }

    private handleGetUserSuccess(user: User) {
        this.isUSUser = user?.CountryCode === 'US';
        if (!user) {
            return;
        }

        this.isDEUser = this.userService.isDEUser(user);
        this.isGBUser = this.userService.isGBUser(user);
        this.isEUUser = this.userService.isEUUser(user.CountryCode);
        this.country = user.CountryCode;
    }

    private handleGetUserError(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingUserInfo'),
            NotificationType.ERROR);
    }

    private handleGetCustomerSuccess(response) {
        this.isDDEnabled = response?.DDEnabled === true;

        this.utilityService.clearSubscriptions([this.getDashboardAnnouncementsSubscription, this.getBannersSubscription]);
        this.isAnnouncementsLoading = true;
        // [MV3-2497] Removed Pipe for Shipping StationID to instead pass the int
        this.getDashboardAnnouncementsSubscription =
            this.announcementService.getAnnouncements(response.ShippingStationId)
                .pipe(takeWhile(() => !this.announcements))
                .subscribe(
                    announcements => this.handleGetDashboardAnnouncementsSuccess(announcements),
                    err => this.handleGetDashboardAnnouncementsFailure(err),
                );

        // low volume = (paid customer) made less than 100 shipments in the previous month.
        const isLowVolumeCustomer = this.customer?.MonthlyTierType === 1 ||
            this.customer?.MonthlyTierType === 2 ||
            this.customer?.MonthlyTierType === 3 || this.customer?.MonthlyTierType === 4;

        // a newly inserted customer data will have the value of null for MonthlyTierType.
        const isNewCustomer = this.customer?.MonthlyTierType === null;

        const canReceiveComplimentaryScore = isLowVolumeCustomer || isNewCustomer;
        const isEligibleForComplimentaryScore =
            canReceiveComplimentaryScore
            && this.isUSUser
            && this.customer?.DDEnabled === false
            && (this.customerSubscription?.CurrentSubType === null);

        this.showGiftBox = (this.customer?.MonthlyTierType === 1 || this.customer?.MonthlyTierType === null) &&
            this.isUSUser &&
            this.customer?.DDEnabled === false;

        if (isEligibleForComplimentaryScore) {
            const dialogConfig: MatDialogConfig = {
                disableClose: true,
                maxWidth: '807px',
                width: '807px',
                data: {
                    editMode: false,
                    monthlyTierType: this.customer?.MonthlyTierType,
                },
            };

            const ddWelcomeCookieId = `dd_welcome_${ this.customer.CustomerId }`;
            const ddWelcomeCookie = this.cookieService.get(ddWelcomeCookieId);
            if (!ddWelcomeCookie) {
                const dialogRef: MatDialogRef<DefencePopupComponent> = this.dialog.open(DefencePopupComponent, dialogConfig);
                dialogRef.afterClosed().subscribe((result) => {
                    this.cookieService.set(
                        ddWelcomeCookieId,
                        'true',
                        15,
                        '/',
                        window.location.hostname,
                        false,
                        'Lax',
                    );
                });
            }
        }

        this.isBannersLoading = true;
        this.getBannersSubscription = this.bannerService.getDashboardBanners(response.ShippingStationId)
                                          .pipe(takeWhile(() => !this.banners))
                                          .subscribe(
                                              banners => this.handleGetDashboardBannersSuccess(banners),
                                              err => this.handleGetDashboardBannersFailure(err),
                                          );
    }

    private handleGetCustomerError(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingCustomerInfo'),
            NotificationType.ERROR);
    }

    private handleGetUserLatestShipmentsSuccess(shipments: IQuickTrackShipment[]) {
        if(shipments.length > 0) {
            this.isEmpty = false;
        } else {
            this.isEmpty = true;
        }
        this.quickTrackShipments = shipments;
        this.isGettingQuickTrackShipments = false;
        this.dtTrigger.next(null);
    }

    private handleGetUserLatestShipmentsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingQuickTrack'),
            NotificationType.ERROR);
        this.isGettingQuickTrackShipments = false;
    }

    private handleGetDashboardAnnouncementsSuccess(announcements: IAnnouncement[]) {
        this.announcements = _.sortBy(announcements, ['SortOrder']);
        this.isAnnouncementsLoading = false;
    }

    private handleGetDashboardAnnouncementsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingAnnouncements'),
            NotificationType.ERROR);
        this.isAnnouncementsLoading = false;
    }

    private handleGetDashboardBannersSuccess(banners: IBanner[]) {
        this.dashboardBannerConfig = {
            speed: 5000,
            isAutoForward: true,
            images: [],
        };

        // [MV3-2314] use images returned from the API.
        // [MV3-2314]: ignore isActive property.
        const activeBanners = banners; // banners.filter(banner => banner.isActive);
        if (activeBanners && activeBanners.length) {
            const desktopBanner = activeBanners.find(banner => banner.BannerType === BannerType.Desktop);
            const mobileBanner = activeBanners.find(banner => banner.BannerType === BannerType.Mobile);
            const sideDashboardBanner = activeBanners.find(banner => banner.BannerType === BannerType.SideDashboard);

            this.dashboardBannerConfig.images.push({
                smallImageUrl: mobileBanner ?
                    mobileBanner.BannerURL :
                    '/assets/images/banners/PP-Members-Banner-B2B-transactaphobia-Web-Mobile-learnmore.jpg',
                mediumImageUrl: desktopBanner ?
                    desktopBanner.BannerURL :
                    '/assets/images/banners/PP-Members-banner-B2B-Transactaphobia-Web-Members-learnmore.jpg',
                largeImageUrl: desktopBanner ?
                    desktopBanner.BannerURL :
                    '/assets/images/banners/PP-Members-banner-B2B-Transactaphobia-Web-Members-learnmore.jpg',
                defaultImageUrl: desktopBanner ?
                    desktopBanner.BannerURL :
                    '/assets/images/banners/PP-Members-banner-B2B-Transactaphobia-Web-Members-learnmore.jpg',
                landingUrl: desktopBanner ?
                    desktopBanner.Hyperlink :
                    'https://upscapital.com/product-services/b2b-secure-payments/',
                sideDashboardImageUrl: sideDashboardBanner ? sideDashboardBanner.BannerURL : '',
                sideDashboardLinkUrl: sideDashboardBanner ? sideDashboardBanner.Hyperlink : '',
            });
            this.isSideBannerAvailable = sideDashboardBanner ? true : false;
        }

        this.isBannersLoading = false;
    }

    private handleGetDashboardBannersFailure(err) {
        // use default images.
        this.dashboardBannerConfig = SiteConfig.dashboardBanner;
        this.isBannersLoading = false;
    }

    private handleGetUpcomingEventsSuccess(events: IEvent[]) {
        this.upcomingEvents = <any>_.sortBy(events, ['EVentStartDate', 'EventStartTime', 'SortOrder']);
        this.isAnnouncementsLoading = false;
    }

    private handleGetUpcomingEventsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingEvents'),
            NotificationType.ERROR);
        this.isAnnouncementsLoading = false;
    }

    private handleGetNewsSuccess(newsItems: INews[]) {
        this.news = _.sortBy(newsItems, ['SortOrder']);
        this.isNewsLoading = false;
    }

    private handleGetNewsFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingNews'),
            NotificationType.ERROR);
        this.isNewsLoading = false;
    }

    private handleGetShipmentSuccess(shipmentPackage: Package) {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            data: {
                shipmentPackage,
                shipmentType: 'web',
            },
        };

        const dialogRef = this.dialog.open(TrackingDetailsDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(
            (result) => {
                if (!result) {
                    return;
                }
            },
        );

        this.isGettingShipmentDetails = false;
    }

    private handleGetShipmentFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('dashboard.failedGettingShipmentDetails'),
            NotificationType.ERROR);
        this.isGettingShipmentDetails = false;
    }
    private isUserFromFrance(): boolean{
        return this.user?.CountryCode === 'FR';
    }

}
